import React from 'react';
import clsx from 'clsx';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import testIds from '@smart-tv/e2e/shared/variables';
import formatDate from '@smart-tv/utils/date/format-date';
import formatTitle from '@smart-tv/utils/formatter/format-title';
import PlayerChannelProgressBar from './player-channel-progress-bar';
import usePlayerChannel from './use-player-channel';
import { Props } from './types';
import styles from './player-channel.module.scss';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Typography from '@smart-tv/components/typography';

export const PlayerChannel: React.FC<Props> = (props: Props) => {
  const { channel, selected } = props;
  const { icon, currentProgram, nextProgram } = channel;
  const { ref, focused, animating, setAnimating, handleEnterPress } = usePlayerChannel(props);
  if (!currentProgram || !nextProgram) return null;

  const linkClassName = clsx(
    styles['player-channel__link'],
    IS_BROWSER && styles['player-channel__link--browser'],
    selected && styles['player-channel__link--selected'],
    focused && styles['player-channel__link--focused'],
    animating && styles['player-channel__link--animating']
  );

  return (
    <li ref={ref} data-testid={testIds.player.channels.channel.root} className={styles['player-channel']} onClick={handleEnterPress}>
      <div className={linkClassName} onAnimationEnd={() => setAnimating(false)}>
        <LazyLoadImage src={icon} data-testid={testIds.player.channels.channel.logo} className={styles['player-channel__logo']}/>
        <div className={styles['player-channel-content']}>
          <div className={styles['player-channel-content__current']}>
            <Typography
              variant="caption2"
              data-testid={testIds.player.channels.channel.timespan}
              className={styles['player-channel-content__timestamp']}
            >
              {`${formatDate(currentProgram.startTime, 'hh:mm')} - ${formatDate(
                currentProgram.endTime,
                'hh:mm'
              )}`}
            </Typography>
            

            <Typography
              variant="caption"
              data-testid={testIds.player.channels.channel.title}
              className={styles['player-channel-content__current-program-title']}
            >
              {formatTitle(currentProgram.title)}
            </Typography>
          </div>
          <PlayerChannelProgressBar
            startTime={currentProgram.startTime}
            endTime={currentProgram.endTime}
          />
          {(focused || IS_BROWSER) && (
            <div className={styles['player-channel-content__future']}>
              <Typography
                variant="caption2"
                data-testid={testIds.player.channels.channel.nextTimespan}
                className={styles['player-channel-content__timestamp']}
              >
                {`${formatDate(nextProgram.startTime, 'hh:mm')} - ${formatDate(
                  nextProgram.endTime,
                  'hh:mm'
                )}`}
              </Typography>
              <Typography
                variant="caption2"
                data-testid={testIds.player.channels.channel.nextTitle}
                className={styles['player-channel-content__next-program-title']}
              >
                {formatTitle(nextProgram.title)}
              </Typography>
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

export default React.memo(PlayerChannel);
